import { rootApi } from "./baseApi";
import type { BaseUnit, BaseUnitType } from "../types";
import type { RootState } from "../store/store";

export interface ProvisioningConfig {
  broker: string;
  port: number;
  username: string;
  password: string;
  customer_id: string;
}

export interface ArbSensor {
  sensor_id: number;
  sensor_name: string;
  sensor_serialNo: number;
}

export interface ArbSettings {
  silent_alarm: boolean;
}

interface AddBaseUnitRequest {
  baseUnitSerialNo: number;
  baseUnitName: string;
  typeId: number;
  typeDescription?: string;
}

interface DeleteBaseUnitRequest {
  baseUnitId: number;
}

interface UpdateBaseUnitRequest {
  baseUnitId: number;
  baseUnitName: string;
}

/** Canonical cache key / query arg so optimistic patches always hit the same entry as hooks. */
function arbSerialCacheKey(serial: string) {
  return String(serial ?? "").trim();
}

export const baseUnitApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getBaseUnitTypes: builder.query<BaseUnitType[], void>({
      query: () => ({
        url: "/base-unit-types",
        method: "GET"
      }),
      providesTags: ['BaseUnitTypes'],
      transformResponse: (response: BaseUnitType[]) => response.map(type => ({
        type: type.type,
        description: type.description
      }))
    }),

    getBaseUnitList: builder.query<BaseUnit[], void>({
      query: () => ({
        url: "/baseUnits",
        method: "GET"
      }),
      providesTags: ['BaseUnits'],
    }),

    addBaseUnit: builder.mutation<{ message: string } & Partial<import('../types').BaseUnit>, AddBaseUnitRequest>({
      query: (newBaseUnit) => ({
        url: "/baseUnits",
        method: "POST",
        body: newBaseUnit,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.id != null) {
            dispatch(
              baseUnitApi.util.updateQueryData('getBaseUnitList', undefined, (draft) => {
                if (draft.some(b => Number(b.id) === Number(data.id))) return;
                draft.push({
                  id: Number(data.id),
                  serialNo: Number(data.serialNo ?? arg.baseUnitSerialNo),
                  base_unit_name: data.base_unit_name ?? arg.baseUnitName,
                  type: data.type ?? arg.typeId,
                  description: data.description ?? arg.typeDescription ?? null,
                  in_contact: false,
                  last_contact: '',
                  silent_alarm: false,
                  is_deleted: false,
                  created_at: new Date().toISOString(),
                  updated_at: null,
                });
              }),
            );
          }
        } catch {}
      },
      // No invalidatesTags — optimistic patch above handles the cache immediately.
      // A background refetch can arrive out of order when adding units rapidly.
    }),

    updateBaseUnit: builder.mutation<BaseUnit, UpdateBaseUnitRequest>({
      query: (updateData) => ({
        url: "/baseUnits",
        method: "PATCH",
        body: updateData,
      }),
      invalidatesTags: ['BaseUnits'],
    }),

    deleteBaseUnit: builder.mutation<{ message: string }, DeleteBaseUnitRequest>({
      query: (deleteData) => ({
        url: "/baseUnits",
        method: "DELETE",
        body: deleteData,
      }),
      async onQueryStarted({ baseUnitId }, { dispatch, queryFulfilled }) {
        const bid = Number(baseUnitId);
        const patch = dispatch(
          baseUnitApi.util.updateQueryData("getBaseUnitList", undefined, (draft) => {
            const idx = draft.findIndex((b) => Number(b.id) === bid);
            if (idx !== -1) draft.splice(idx, 1);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      // No invalidatesTags — avoid slow refetch; optimistic removal stays in sync (same idea as sensor deleteSensor).
    }),

    getArbSensors: builder.query<ArbSensor[], string>({
      query: (arbSerial) => ({
        url: `/alarm-units/${encodeURIComponent(arbSerial)}/sensors`,
        method: "GET",
      }),
      providesTags: (_result, _err, arbSerial) => [{ type: 'ArbSensors' as const, id: arbSerial }],
    }),

    assignArbSensor: builder.mutation<{ message: string }, { arbSerial: string; sensorId: number; sensorName?: string; sensorSerialNo?: number }>({
      query: ({ arbSerial, sensorId }) => ({
        url: `/alarm-units/${encodeURIComponent(arbSerial)}/sensors`,
        method: "POST",
        body: { sensorId },
      }),
      async onQueryStarted(
        { arbSerial, sensorId, sensorName, sensorSerialNo },
        { dispatch, queryFulfilled, getState }
      ) {
        const key = arbSerialCacheKey(arbSerial);
        // Drop any in-flight list request so a slow GET cannot land after the optimistic patch and wipe it.
        const running = dispatch(
          rootApi.util.getRunningQueryThunk("getArbSensors", key)
        ) as { abort?: () => void } | undefined;
        running?.abort?.();

        const newSensor: ArbSensor = {
          sensor_id: sensorId,
          sensor_name: sensorName ?? "",
          sensor_serialNo: sensorSerialNo ?? 0,
        };
        const prior = baseUnitApi.endpoints.getArbSensors.select(key)(getState() as RootState).data;

        const next: ArbSensor[] = Array.isArray(prior)
          ? prior.some((s) => Number(s.sensor_id) === Number(sensorId))
            ? prior
            : [...prior, newSensor]
          : [newSensor];

        const patch = dispatch(
          baseUnitApi.util.upsertQueryData("getArbSensors", key, next)
        );
        try {
          await queryFulfilled;
          dispatch(
            baseUnitApi.endpoints.getArbSensors.initiate(key, {
              subscribe: false,
              forceRefetch: true,
            })
          );
        } catch {
          patch.undo();
        }
      },
    }),

    removeArbSensor: builder.mutation<{ message: string }, { arbSerial: string; sensorId: number }>({
      query: ({ arbSerial, sensorId }) => ({
        url: `/alarm-units/${encodeURIComponent(arbSerial)}/sensors/${sensorId}`,
        method: "DELETE",
      }),
      async onQueryStarted({ arbSerial, sensorId }, { dispatch, queryFulfilled, getState }) {
        const key = arbSerialCacheKey(arbSerial);
        const running = dispatch(
          rootApi.util.getRunningQueryThunk("getArbSensors", key)
        ) as { abort?: () => void } | undefined;
        running?.abort?.();

        const prior = baseUnitApi.endpoints.getArbSensors.select(key)(getState() as RootState).data;

        const noopUndo = { undo: () => {} };
        const patch = Array.isArray(prior)
          ? dispatch(
              baseUnitApi.util.upsertQueryData(
                "getArbSensors",
                key,
                prior.filter((s) => Number(s.sensor_id) !== Number(sensorId))
              )
            )
          : noopUndo;
        try {
          await queryFulfilled;
          dispatch(
            baseUnitApi.endpoints.getArbSensors.initiate(key, {
              subscribe: false,
              forceRefetch: true,
            })
          );
        } catch {
          patch.undo();
        }
      },
    }),

    getArbSettings: builder.query<ArbSettings, string>({
      query: (arbSerial) => ({
        url: `/alarm-units/${encodeURIComponent(arbSerial)}/settings`,
        method: "GET",
      }),
      providesTags: (_result, _err, arbSerial) => [{ type: 'ArbSettings' as const, id: arbSerial }],
    }),

    getProvisioningConfig: builder.query<ProvisioningConfig, void>({
      query: () => ({
        url: '/provisioning-config',
        method: 'GET',
      }),
    }),

    updateArbSettings: builder.mutation<{ message: string; silent_alarm: boolean }, { arbSerial: string; silent_alarm: boolean }>({
      query: ({ arbSerial, silent_alarm }) => ({
        url: `/alarm-units/${encodeURIComponent(arbSerial)}/settings`,
        method: "PATCH",
        body: { silent_alarm },
      }),
      invalidatesTags: (_result, _err, { arbSerial }) => ['BaseUnits', { type: 'ArbSettings' as const, id: arbSerial }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBaseUnitTypesQuery,
  useGetBaseUnitListQuery,
  useAddBaseUnitMutation,
  useUpdateBaseUnitMutation,
  useDeleteBaseUnitMutation,
  useGetArbSensorsQuery,
  useAssignArbSensorMutation,
  useRemoveArbSensorMutation,
  useGetArbSettingsQuery,
  useUpdateArbSettingsMutation,
  useGetProvisioningConfigQuery,
} = baseUnitApi;